/**
 * Lightweight data-versions fetcher for CDN cache busting.
 *
 * Fetches /your_tour_api/data-versions/{urlSlug} (which bypasses CDN)
 * and returns version hashes that page components append as ?v= params
 * to their data URLs, ensuring CDN serves fresh data after content changes.
 *
 * The result is cached per urlSlug and mode for the lifetime of the page —
 * cleared automatically on browser refresh. Mode is part of the key because
 * preview mode hashes unpublished content too: sharing one entry would let a
 * preview session bust its caches off the published hashes and never notice
 * an unpublished node changing, and vice versa.
 */

import { buildYourTourApiUrl, fetchYourTourApi } from './ApiHelpers';
import { getApiMode } from './previewMode';
import { useAppStore } from '../stores';

// Module-level cache: { [`${urlSlug}::${mode}`]: { promise, versions } }
const cache = {};

/**
 * Build the cache key. Mode is normalised to '' when preview is not in play,
 * so a normal session always uses the same key regardless of query string.
 */
function cacheKey( urlSlug, mode ) {
  return `${urlSlug}::${mode || ''}`;
}

/**
 * The mode this fetch should use, read from the app store.
 */
function currentMode( platform ) {
  const { settings } = useAppStore.getState();
  return getApiMode( platform ? { ...settings, platform } : settings );
}

/**
 * Fetch data versions for a community, returning the cached result if available.
 * Deduplicates concurrent requests for the same urlSlug and mode.
 */
async function fetchVersions( urlSlug, platform ) {
  const mode = currentMode( platform );
  const key = cacheKey( urlSlug, mode );

  if ( cache[key]?.versions ) {
    return cache[key].versions;
  }

  // Deduplicate: if a request is already in flight, wait for it.
  if ( cache[key]?.promise ) {
    return cache[key].promise;
  }

  const promise = ( async () => {
    try {
      const url = buildYourTourApiUrl(`/your_tour_api/data-versions/${urlSlug}`, {}, platform);
      const data = await fetchYourTourApi( url, platform );
      const versions = data?.versions || {};

      // This endpoint is fetched by every page and bypasses the CDN, so its
      // previewMode flag is the authoritative answer to "did the server
      // honour preview?". Record it even when preview was not requested, so
      // the banner can tell "not asked for" from "asked for and refused".
      if ( mode ) {
        useAppStore.getState().setPreviewGranted( Boolean( data?.previewMode ) );
      }

      cache[key] = { versions };
      return versions;
    } catch ( error ) {
      console.warn( '[dataVersions] Failed to fetch data versions:', error );
      // Cache empty object so we don't retry on every component mount.
      cache[key] = { versions: {} };
      if ( mode ) {
        useAppStore.getState().setPreviewGranted( false );
      }
      return {};
    }
  } )();

  cache[key] = { promise };
  return promise;
}

/**
 * Get the version hash for a specific data type.
 *
 * @param {string} urlSlug - Community URL slug.
 * @param {string} versionKey - Key from the versions response
 *   ('amenities', 'floorPlans', 'media', 'map', etc.)
 * @param {string} [platform] - Optional platform override.
 * @returns {Promise<string|null>} The version hash, or null if unavailable.
 */
export async function getDataVersion( urlSlug, versionKey, platform ) {
  if ( !urlSlug ) return null;

  const versions = await fetchVersions( urlSlug, platform );
  return versions[versionKey] || null;
}

/**
 * Clear cached versions so the next getDataVersion() refetches.
 *
 * Call this whenever something learns that the server-side data changed — the
 * heartbeat poller being the live case. Without it the module cache holds the
 * page-load hash forever, so a heartbeat-driven refetch requests
 * `/map/{slug}?v=<old hash>` and the CDN can serve back exactly the stale
 * payload the heartbeat fired to replace.
 *
 * @param {string} [urlSlug] - Clear only this community's entries (every mode).
 *   Omit to clear everything, which is what tests and a full reset want.
 */
export function resetDataVersionsCache( urlSlug ) {
  const prefix = urlSlug ? `${urlSlug}::` : null;
  Object.keys( cache ).forEach( ( key ) => {
    if ( !prefix || key.startsWith( prefix ) ) {
      delete cache[key];
    }
  } );
}
